/**
 * Форматы телефонов поддерживаемых стран — единственный источник для
 * `InputPhoneAll`, форматирования и проверки полноты номера.
 * `mask` — маска национальной части, `padding` — отступ под код страны в поле.
 */
export const PHONE_FORMATS = [
  {
    value: 'ru',
    dial: '+7',
    trunkPrefix: '8',
    mask: '(___) ___-__-__',
    nationalDigits: 10,
    padding: { large: '46px', medium: '44px', small: '44px' },
  },
  {
    value: 'mn',
    dial: '+976',
    trunkPrefix: null,
    mask: '____-____',
    nationalDigits: 8,
    padding: { large: '64px', medium: '60px', small: '60px' },
  },
  {
    value: 'uz',
    dial: '+998',
    trunkPrefix: null,
    mask: '(__) ___-__-__',
    nationalDigits: 9,
    padding: { large: '66px', medium: '62px', small: '62px' },
  },
]

/** Код страны инстанса (`DEFAULT_COUNTRY`), как в `utils/currency`. */
export function getPhoneCountry() {
  const country = process.env.DEFAULT_COUNTRY

  return country ? String(country).toLowerCase() : 'ru'
}

/** Формат страны — страны инстанса по умолчанию. */
export function getPhoneFormat(country = getPhoneCountry()) {
  return (
    PHONE_FORMATS.find((format) => format.value === country) || PHONE_FORMATS[0]
  )
}

/**
 * Наложение маски на национальную часть номера: `'9991234567'` + `'(___) ___-__-__'`
 * → `'(999) 123-45-67'`. Незаполненные хвосты маски не дописываются:
 * `'999'` → `'(999'` — как ведёт себя поле при наборе.
 * @param {string} value
 * @param {string} mask
 * @returns {string}
 */
export function applyPhoneMask(value, mask) {
  const numbers = String(value || '').replace(/\D/g, '')
  if (!numbers) return ''

  let formatted = ''
  let index = 0

  for (let i = 0; i < mask.length; i += 1) {
    const symbol = mask[i]

    if (symbol === '_') {
      if (index < numbers.length) {
        formatted += numbers[index]
        index += 1
      }
    } else if (symbol === '(') {
      // Открывающая скобка появляется сразу, как только есть цифра
      if (numbers.length > 0) formatted += symbol
    } else {
      // Остальные разделители — только когда цифр больше, чем до них в маске
      const digitsBefore = (mask.slice(0, i).match(/_/g) || []).length

      if (digitsBefore > 0 && digitsBefore < numbers.length) formatted += symbol
    }
  }

  return formatted
}

/**
 * Национальная часть номера: без кода страны и без выходной «8».
 * @param {string} value
 * @param {string} [country]
 * @returns {string}
 */
export function getPhoneNationalDigits(value, country = getPhoneCountry()) {
  const format = getPhoneFormat(country)
  const dial = format.dial.replace(/\D/g, '')

  let digits = String(value || '').replace(/\D/g, '')
  if (!digits) return ''

  if (digits.length > format.nationalDigits && digits.startsWith(dial)) {
    digits = digits.slice(dial.length)
  } else if (
    format.trunkPrefix &&
    digits.length === format.nationalDigits + 1 &&
    digits.startsWith(format.trunkPrefix)
  ) {
    digits = digits.slice(format.trunkPrefix.length)
  }

  return digits.slice(0, format.nationalDigits)
}

/**
 * Номер без единой цифры (пустая строка, один «+», маска без цифр).
 * @param {string} value
 * @returns {boolean}
 */
export function isPhoneEmpty(value) {
  return String(value || '').replace(/\D/g, '').length === 0
}

/**
 * Полный ли номер. Страна определяется по коду внутри самого номера
 * (+7 / +976 / +998), поэтому монгольский номер из 8 цифр не считается неполным.
 * Цифры без кода страны сравниваются с длиной номера страны инстанса.
 * @param {string} value
 * @returns {boolean}
 */
export function isPhoneComplete(value) {
  if (isPhoneEmpty(value)) return false

  const digits = String(value).replace(/\D/g, '')

  // Код страны внутри номера: «+976 9911-2233», «79991234567»
  const withDial = PHONE_FORMATS.some((format) => {
    const dial = format.dial.replace(/\D/g, '')

    return (
      digits.length === dial.length + format.nationalDigits &&
      digits.startsWith(dial)
    )
  })

  if (withDial) return true

  // Российский выход на межгород: 8XXXXXXXXXX
  const withTrunk = PHONE_FORMATS.some(
    (format) =>
      format.trunkPrefix &&
      digits.length === format.nationalDigits + 1 &&
      digits.startsWith(format.trunkPrefix)
  )

  if (withTrunk) return true

  // Код страны набран, но номер не дописан
  const hasDialPrefix = PHONE_FORMATS.some((format) =>
    digits.startsWith(format.dial.replace(/\D/g, ''))
  )

  if (hasDialPrefix) return false

  return digits.length === getPhoneFormat().nationalDigits
}

/**
 * Формат номера: по коду страны внутри номера («+976 9911-2233» → Монголия),
 * иначе — формат страны инстанса. Учитывает российскую выходную «8».
 * @param {string} value
 * @param {string} [country]
 */
export function resolvePhoneFormat(value, country = getPhoneCountry()) {
  const raw = String(value || '')

  const explicit = PHONE_FORMATS.find((format) => raw.includes(format.dial))
  if (explicit) return explicit

  const digits = raw.replace(/\D/g, '')

  const withTrunk = PHONE_FORMATS.find(
    (format) =>
      format.trunkPrefix &&
      digits.length === format.nationalDigits + 1 &&
      digits.startsWith(format.trunkPrefix)
  )

  return withTrunk || getPhoneFormat(country)
}

/**
 * Цифры номера с кодом страны — ключ для поиска и сравнения клиентов:
 * «8 (999) 123-45-67» → «79991234567», «+976 9911-2233» → «97699112233».
 * Неполный номер возвращается цифрами как есть.
 * @param {string} value
 * @param {string} [country]
 * @returns {string}
 */
export function getPhoneDialDigits(value, country = getPhoneCountry()) {
  const digits = String(value || '').replace(/\D/g, '')
  if (!digits) return ''

  const format = resolvePhoneFormat(value, country)
  const total = format.dial.replace(/\D/g, '').length + format.nationalDigits

  // Хвост «доб. 123» в конце: у слишком длинной строки берём последние цифры номера
  if (digits.length > total) return digits.slice(-total)

  const national = getPhoneNationalDigits(digits, format.value)

  if (national.length !== format.nationalDigits) return digits

  return `${format.dial.replace(/\D/g, '')}${national}`
}

/**
 * Номер в формате страны: «+7 (999) 123-45-67», «+976 9911-2233».
 * Страна определяется по коду внутри номера, неполный номер возвращается как есть.
 * @param {string} value
 * @param {string} [country]
 * @returns {string}
 */
export function formatPhone(value, country = getPhoneCountry()) {
  const format = resolvePhoneFormat(value, country)
  const national = getPhoneNationalDigits(value, format.value)

  if (national.length !== format.nationalDigits) return String(value || '')

  return `${format.dial} ${applyPhoneMask(national, format.mask)}`
}

/**
 * Свободный телефон из БЗ → строка для InputPhoneAll (+7 / +976 / +998).
 * «8…» → +7; цифры без кода страны — под страну инстанса (`DEFAULT_COUNTRY`),
 * чтобы номер был виден в форме клиента.
 * @param {string} value
 * @param {string} [country]
 */
export function normalizePhoneForInputPhoneAll(
  value,
  country = getPhoneCountry()
) {
  const raw = String(value || '').trim()
  if (!raw) return ''

  const formats = [...PHONE_FORMATS].sort(
    (a, b) => b.dial.length - a.dial.length
  )

  for (const format of formats) {
    if (raw.startsWith(format.dial)) {
      return `${format.dial} ${raw.slice(format.dial.length).trim()}`.trim()
    }
  }

  let digits = raw.replace(/\D/g, '')
  if (!digits) return ''

  // Российский выход на межгород: 8XXXXXXXXXX → 7…
  const trunkFormat = formats.find(
    (format) =>
      format.trunkPrefix &&
      digits.length > format.nationalDigits &&
      digits.startsWith(format.trunkPrefix)
  )

  if (trunkFormat) {
    const dial = trunkFormat.dial.replace(/\D/g, '')

    digits = `${dial}${digits.slice(trunkFormat.trunkPrefix.length)}`
  }

  // Цифры, начинающиеся с кода страны: 7…, 976…, 998…
  for (const format of formats) {
    const dial = format.dial.replace(/\D/g, '')

    if (digits.length > format.nationalDigits && digits.startsWith(dial)) {
      return `${format.dial} ${digits.slice(dial.length)}`.trim()
    }
  }

  return `${getPhoneFormat(country).dial} ${digits}`.trim()
}
